import type {
  BlockStateDefinition,
  ImplicitDependencyGroup,
} from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type {
  BlockStateUnionType,
  BlockType,
  ComponentTypeUnionType,
} from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import _omit from 'lodash/omit';
import type {
  NestedTableBlockStateType,
  NestedTableComponentType,
} from '@unifyapps/defs/blocks/Table/NestedTable/types';
import { lazy } from 'react';
import type { Action } from '@unifyapps/defs/types/action';
import { getTableEventTargetIds } from '../commons/getEventTargetIds';
import { clearChangeset } from '../DataTable/utils/changeset';
import type { DataTableBlockRef } from '../DataTable/types';

const importNestedTableComponent = () =>
  import(
    /* webpackChunkName: "nested-table-block" */
    './NestedTable'
  );

const NestedTable = lazy(importNestedTableComponent);

class NestedTableBlockStateDefinition implements BlockStateDefinition {
  useBlockRef?: boolean | undefined = true;

  get type(): ComponentTypeUnionType {
    return 'NestedTable';
  }

  getEventTargetIds(block: BlockType) {
    return getTableEventTargetIds(block);
  }

  getBlockControlMethods() {
    return [
      {
        id: 'clearChangeset' as const,
        label: 'Clear Changeset',
        method: (blockState: BlockStateUnionType, action: Action) => {
          const tableBlockState = blockState as NestedTableBlockStateType;
          const payload = action.payload as { methodPayload?: { changesetCursor: number } };

          return {
            ...tableBlockState,
            ...clearChangeset({
              changesetMap: tableBlockState.changesetMap,
              changesetList: tableBlockState.changesetList,
              identifier: tableBlockState.content.identifier,
              changesetCursor: payload.methodPayload?.changesetCursor,
            }),
          } as BlockStateUnionType;
        },
      },
      {
        id: 'refetchData' as const,
        label: 'Refetch Data',
        method: async (_: BlockStateUnionType, __: Action, blockRef?: DataTableBlockRef) => {
          await blockRef?.refetch?.();
          return Promise.resolve();
        },
      },
    ];
  }

  get initialStateGetter() {
    return (block: BlockType) =>
      ({
        id: block.id,
        ..._pick(block.component, ['appearance', 'componentType', 'slots']),
        content: {
          ..._omit((block.component as NestedTableComponentType).content, ['addOns']),
          ...(block.component as NestedTableComponentType).content.addOns,
        },
        ..._pick((block.component as NestedTableComponentType).content, ['addOns']),
      }) as BlockStateUnionType;
  }

  getComponent() {
    return NestedTable;
  }

  preload() {
    return {
      blockPreload: importNestedTableComponent,
    };
  }

  getImplicitDependencyGroups(): ImplicitDependencyGroup {
    return { isEntitySuspended: true, usingDataSourcesResponse: true };
  }
}

export default NestedTableBlockStateDefinition;
